import Text, {
    TextDomTag,
} from 'client/widget-components/basicComponents/Text';
import Textarea, {
    TextareaProps,
} from 'client/widget-components/basicComponents/Textarea';
import { textAreaDefaultStyles } from 'client/widget-components/components/TextField/styles';
import {
    ResponsiveStylesProp,
    mergeResponsiveStyles,
    nestResponsiveStyles,
    pickResponsiveStyles,
} from 'client/widget-components/responsive-styles/responsiveStylesService';
import React, { CSSProperties, useMemo } from 'react';
import styled from 'styled-components';
import type { ResponsiveStylesStructured } from '@duda-co/responsive-styles';
import { RuntimeMediaQuery } from '@duda-co/responsive-styles/RuntimeMediaQuery';
import { TEXT_FIELD_DATA_GRABS } from '../../constants';

interface TextFieldCustomizationTextAreaProps
    extends Omit<TextareaProps, 'styles'> {
    styles?: {
        textArea?: ResponsiveStylesProp;
        placeholder?: ResponsiveStylesProp;
    };
    error?: boolean;
}

// Placeholder should inherit global Paragraph styles `div.dmNewParagraph p`
const TextFieldCustomizationTextArea = (
    props: TextFieldCustomizationTextAreaProps
) => {
    const { styles, error, placeholder, value } = props;
    
    const { textareaStyles, placeholderStyles } = useMemo(() => {
        const _textareaStyles = mergeResponsiveStyles([
            textAreaDefaultStyles,
            error ? invalidTextareaStyles : {},
            nestResponsiveStyles(
                {
                    [RuntimeMediaQuery.COMMON]: {
                        opacity: 0,
                    },
                },
                {
                    innerSelector: '&::placeholder',
                }
            ),
            styles?.textArea,
        ]);

        return {
            textareaStyles: _textareaStyles,
            placeholderStyles: mergeResponsiveStyles([
                pickResponsiveStyles(_textareaStyles, propertiesSetToPick),
                hintDefaultStyles,
                styles?.placeholder,
            ]),
        };
    }, [styles?.textArea, styles?.placeholder, error]);

    return (
        <TextAreaWrapper className='dmNewParagraph'>
            <Textarea {...props} styles={textareaStyles} />
            {!value && (
                <Text
                    styles={placeholderStyles}
                    data-grab={
                        TEXT_FIELD_DATA_GRABS.textareaPlaceholderDataGrab
                    }
                    tag={TextDomTag.paragraph}
                >
                    {placeholder}
                </Text>
            )}
        </TextAreaWrapper>
    );
};

export default TextFieldCustomizationTextArea;

const TextAreaWrapper = styled.div`
    height: 86px;
    position: relative;
    margin: 0;
    padding: 0;
`;

const invalidTextareaStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        borderColor: '#E33E3B',
    },
};

const commonPlaceholderStyles: CSSProperties = {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    boxSizing: 'border-box',
    pointerEvents: 'none',
    margin: 0,
    borderColor: 'transparent',
    overflow: 'hidden',
    color: 'rgba(201, 202, 205, 1)',
};

const hintDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: commonPlaceholderStyles,
    [RuntimeMediaQuery.MOBILE]: commonPlaceholderStyles,
    [RuntimeMediaQuery.TABLET]: commonPlaceholderStyles,
    [RuntimeMediaQuery.DESKTOP]: commonPlaceholderStyles,
    [RuntimeMediaQuery.MOBILE_IMPLICIT]: commonPlaceholderStyles,
    [RuntimeMediaQuery.TABLET_IMPLICIT]: commonPlaceholderStyles,
};

const propertiesSetToPick: Set<keyof CSSProperties> = new Set([
    'border',
    'borderWidth',
    'borderTopWidth',
    'borderBottomWidth',
    'borderLeftWidth',
    'borderRightWidth',

    'borderRadius',
    'borderTopLeftRadius',
    'borderTopRightRadius',
    'borderBottomLeftRadius',
    'borderBottomRightRadius',

    'padding',
    'paddingTop',
    'paddingRight',
    'paddingBottom',
    'paddingLeft',
    'paddingInline',
    'paddingBlock',
    'paddingInlineStart',
    'paddingInlineEnd',
    'paddingBlockStart',
    'paddingBlockEnd',
]);
